<template>
  <div v-if="shouldShowRainCanvas" class="rain-canvas">
    <Transition name="top-bar-drop" appear>
      <div class="top-bar">
        <div class="collect-bar">
          <div class="tip">
            {{ $t('已获得') }}
            <span class="count-wrap">
              <span class="count" :class="{ hold: isCountJumping }">
                <span class="count-inner" :class="{ flash: isCountFlashing }">
                  {{ hitCount }}
                </span>
              </span>
            </span>
            {{ $t('个红包') }}
          </div>
        </div>
        <div class="time-block">
          {{ $t('倒计时') }}
          <div class="time-text">{{ remainingTimeText }}</div>
        </div>

        <div class="close-btn" @click="handleClose">
          <SvgIcon name="svg/close.svg" size="25px" :fill="false" />
        </div>
      </div>
    </Transition>

    <canvas ref="canvasRef" class="canvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import SvgIcon from '@/components/SvgIcon.vue'
import { useRedPacketRain } from '@/composables/useRedPacketRain'
import packetUrl from '@/assets/imgs/activity/rain/packet.webp'
import shadowUrl from '@/assets/imgs/activity/rain/shadow.webp'
import lanternUrl from '@/assets/imgs/activity/rain/lantern.webp'
import miniPacketUrl from '@/assets/imgs/activity/rain/mini_packet.webp'
import coinUrl from '@/assets/imgs/activity/rain/dropping_coin.webp'

const { shouldShowRainCanvas, animationDuration, finishRain } = useRedPacketRain()
const { t } = useI18n()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const hitCount = ref(0)
const isCountJumping = ref(false)
const isCountFlashing = ref(false)
const remainingSeconds = ref(0)

let ctx: CanvasRenderingContext2D | null = null
let W = 0
let H = 0
let running = false
let rafId: number | null = null
let endTime = 0
let envelopeCanvas: HTMLCanvasElement | null = null
let miniPacketCanvas: HTMLCanvasElement | null = null
let lanternBlurCanvases: Array<HTMLCanvasElement | null> = []
let coinBlurCanvases: Array<HTMLCanvasElement | null> = []
let interruptionHandled = false
let countJumpTimer: number | null = null
let countFlashTimer: number | null = null
const items: Money[] = []
const decorItems: DecorItem[] = []
const PACKET_WIDTH = 66
const PACKET_HEIGHT = 75
const LANTERN_WIDTH = 44
const LANTERN_HEIGHT = 45
// mini_packet 尺寸
const MINI_PACKET_WIDTH = 39
const MINI_PACKET_HEIGHT = 69
// dropping_coin 尺寸
const COIN_WIDTH = 96
const COIN_HEIGHT = 98

// 装饰物比例（相对于红包数量的 60%）
const DECOR_RATIO_TO_PACKET = 0.6
// 装饰物内部比例分配（灯笼:迷你红包:金币）
const DECOR_LANTERN_RATIO = 0.4
const DECOR_MINI_PACKET_RATIO = 0.3
const DECOR_COIN_RATIO = 0.3

// 景深效果参数
const DEPTH_SCALE_MIN = 0.9
const DEPTH_SCALE_MAX = 0.99
const DEPTH_BLUR_MIN = 1
const DEPTH_BLUR_MAX = 6
const DEPTH_BLUR_LEVELS = [2, 4, 6]
// 景深效果触发概率（30%的装饰物会有景深效果）
const DEPTH_EFFECT_PROBABILITY = 0.3
const BASE_RAIN_COUNT = 60
const MIN_RAIN_COUNT = 38
const MAX_RAIN_COUNT = 70
const BASE_AREA = 390 * 844
const SPEED_MIN = 1.6
const SPEED_MAX = 3.0
const DRIFT_MAX = 0.5
const COUNT_JUMP_HOLD_MS = 180
const COUNT_FLASH_MS = 140
const RECYCLE_GAP = 80
const DECOR_RECYCLE_GAP = 110
const COLLECT_TARGET_Y_RATIO = 0.12
const COLLECT_TARGET_Y_MIN = 64
// 控制大屏高分设备的 Canvas 像素量，兼顾清晰度和绘制性能。
const MAX_DEVICE_PIXEL_RATIO = 2
let collectTargetX = 0
let collectTargetY = 0

const createColumnSampler = () => {
  let cursor = 0
  return {
    reset() {
      cursor = 0
    },
    nextX(width: number, padding = 0) {
      const usable = Math.max(1, width - padding * 2)
      const columns = Math.max(5, Math.round(usable / 120))
      const columnWidth = usable / columns
      const column = cursor % columns
      cursor = (cursor + 1) % columns
      const jitter = (Math.random() - 0.5) * columnWidth * 0.6
      const x = padding + columnWidth * (column + 0.5) + jitter
      return Math.min(width - padding - 1, Math.max(padding + 1, x))
    }
  }
}

const collectableColumnSampler = createColumnSampler()
const decorColumnSampler = createColumnSampler()

const getRainCount = (width: number, height: number) => {
  const areaRatio = (width * height) / BASE_AREA
  const count = Math.round(BASE_RAIN_COUNT * areaRatio)
  return Math.min(MAX_RAIN_COUNT, Math.max(MIN_RAIN_COUNT, count))
}

const packetImg = new Image()
packetImg.src = packetUrl

const tailImg = new Image()
tailImg.src = shadowUrl

const lanternImg = new Image()
lanternImg.src = lanternUrl

const miniPacketImg = new Image()
miniPacketImg.src = miniPacketUrl

const coinImg = new Image()
coinImg.src = coinUrl

packetImg.addEventListener('load', () => {
  createEnvelopeCanvas()
})

miniPacketImg.addEventListener('load', () => {
  createMiniPacketCanvas()
})

lanternImg.addEventListener('load', () => {
  createBlurCanvases()
})

coinImg.addEventListener('load', () => {
  createBlurCanvases()
})

// 剩余时间文本
const remainingTimeText = computed(() => {
  return `${remainingSeconds.value}s`
})

/**
 * 设置Canvas高清处理（devicePixelRatio）
 * 解决高分屏（Retina屏）下的绘图模糊问题
 */
function setupHighDPRCanvas(canvas: HTMLCanvasElement, width: number, height: number) {
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO)
  canvas.width = width * dpr
  canvas.height = height * dpr
  canvas.style.width = width + 'px'
  canvas.style.height = height + 'px'

  // 改为 alpha: true 以便显示 CSS 背景渐变
  const context = canvas.getContext('2d', { alpha: true })
  if (!context) return null

  context.setTransform(1, 0, 0, 1, 0, 0)
  context.scale(dpr, dpr)
  return context
}

/**
 * 处理窗口resize，重新设置Canvas
 */
function handleResize() {
  if (!canvasRef.value) return
  W = window.innerWidth
  H = window.innerHeight
  ctx = setupHighDPRCanvas(canvasRef.value, W, H)
  // 重新创建红包图片（因为Canvas尺寸变了）
  createEnvelopeCanvas()
  createMiniPacketCanvas()
  updateCollectTarget()
}

/**
 * 创建红包图片Canvas（高清版）
 */
function createEnvelopeCanvas() {
  if (!packetImg.complete) return
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO)
  const bc = document.createElement('canvas')
  const baseW = PACKET_WIDTH
  const baseH = PACKET_HEIGHT
  bc.width = baseW * dpr
  bc.height = baseH * dpr

  const b = bc.getContext('2d')
  if (!b) return

  b.scale(dpr, dpr)
  b.drawImage(packetImg, 0, 0, baseW, baseH)
  envelopeCanvas = bc
}

/**
 * 创建迷你红包图片Canvas（高清版）
 */
function createMiniPacketCanvas() {
  if (!miniPacketImg.complete) return
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO)
  const sc = document.createElement('canvas')
  sc.width = MINI_PACKET_WIDTH * dpr
  sc.height = MINI_PACKET_HEIGHT * dpr
  const sb = sc.getContext('2d')
  if (!sb) return
  sb.scale(dpr, dpr)
  sb.drawImage(miniPacketImg, 0, 0, MINI_PACKET_WIDTH, MINI_PACKET_HEIGHT)
  miniPacketCanvas = sc
}

function createBlurCanvas(img: HTMLImageElement, width: number, height: number, blurPx: number) {
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO)
  const bc = document.createElement('canvas')
  bc.width = width * dpr
  bc.height = height * dpr
  const b = bc.getContext('2d')
  if (!b) return null
  b.scale(dpr, dpr)
  b.filter = `blur(${blurPx}px)`
  b.drawImage(img, 0, 0, width, height)
  return bc
}

function createBlurCanvases() {
  if (lanternImg.complete) {
    lanternBlurCanvases = DEPTH_BLUR_LEVELS.map((blurPx) =>
      createBlurCanvas(lanternImg, LANTERN_WIDTH, LANTERN_HEIGHT, blurPx)
    )
  }
  if (coinImg.complete) {
    coinBlurCanvases = DEPTH_BLUR_LEVELS.map((blurPx) =>
      createBlurCanvas(coinImg, COIN_WIDTH, COIN_HEIGHT, blurPx)
    )
  }
}

function updateCollectTarget() {
  const width = W || window.innerWidth
  const height = H || window.innerHeight
  collectTargetX = width / 2
  collectTargetY = Math.max(COLLECT_TARGET_Y_MIN, height * COLLECT_TARGET_Y_RATIO)
}

function getBlurIndex(blurValue: number) {
  if (DEPTH_BLUR_LEVELS.length === 1) return 0
  const clamped = Math.min(DEPTH_BLUR_MAX, Math.max(DEPTH_BLUR_MIN, blurValue))
  const ratio = (clamped - DEPTH_BLUR_MIN) / (DEPTH_BLUR_MAX - DEPTH_BLUR_MIN)
  return Math.round(ratio * (DEPTH_BLUR_LEVELS.length - 1))
}

/**
 * 红包类
 */
class Money {
  x = 0
  y = 0
  vy = 0
  vx = 0
  r = 0
  tailOffsetYDelta = 0
  collected = false
  dead = false

  // 注入外部依赖
  private onCollected?: () => void

  constructor(onCollected?: () => void) {
    this.onCollected = onCollected
    this.reset(true)
  }

  reset(isInit = false) {
    const currentW = W || window.innerWidth
    const currentH = H || window.innerHeight
    const edgePadding = 25
    this.x = collectableColumnSampler.nextX(currentW, edgePadding)
    if (isInit) {
      this.y = -Math.random() * currentH
    } else {
      this.y = -PACKET_HEIGHT - Math.random() * RECYCLE_GAP
    }
    this.vy = SPEED_MIN + Math.random() * (SPEED_MAX - SPEED_MIN)
    this.vx = (Math.random() - 0.5) * DRIFT_MAX
    this.r = (Math.random() - 0.5) * 0.4
    this.tailOffsetYDelta = (Math.random() - 0.5) * 16
    this.collected = false
    this.dead = false
  }

  update(targetX: number, targetY: number) {
    if (this.collected) {
      this.x += (targetX - this.x) * 0.18
      this.y += (targetY - this.y) * 0.18
      this.r *= 0.85
      if (Math.abs(targetX - this.x) < 2 && Math.abs(targetY - this.y) < 2) {
        this.onCollected?.()
        this.reset()
      }
      return
    }
    this.y += this.vy
    this.x += this.vx
    if (this.y > H + 140) this.reset()
  }

  draw() {
    if (!ctx) return
    ctx.save()
    ctx.translate(this.x, this.y)
    ctx.rotate(this.r)

    if (!this.collected && !this.dead && tailImg.complete) {
      ctx.globalAlpha = 0.9
      const tailX = -tailImg.width / 2
      const tailY = -tailImg.height + 20 + this.tailOffsetYDelta
      ctx.drawImage(tailImg, tailX, tailY)
      ctx.globalAlpha = 1
    }

    if (envelopeCanvas) {
      ctx.drawImage(
        envelopeCanvas,
        -PACKET_WIDTH / 2,
        -PACKET_HEIGHT / 2,
        PACKET_WIDTH,
        PACKET_HEIGHT
      )
    } else if (packetImg.complete) {
      ctx.drawImage(packetImg, -PACKET_WIDTH / 2, -PACKET_HEIGHT / 2, PACKET_WIDTH, PACKET_HEIGHT)
    }
    ctx.restore()
  }
}

type DecorType = 'packet' | 'lantern' | 'coin'

class DecorItem {
  x = 0
  y = 0
  vy = 0
  vx = 0
  r = 0
  tailOffsetYDelta = 0
  type: DecorType
  depthScale = 1
  blurIndex = -1

  constructor(type: DecorType) {
    this.type = type
    this.reset(true)
  }

  reset(isInit = false) {
    const currentW = W || window.innerWidth
    const currentH = H || window.innerHeight
    this.x = decorColumnSampler.nextX(currentW, 8)

    // 根据类型确定回收高度
    let itemHeight = MINI_PACKET_HEIGHT
    if (this.type === 'lantern') itemHeight = LANTERN_HEIGHT
    else if (this.type === 'coin') itemHeight = COIN_HEIGHT

    if (isInit) {
      this.y = -Math.random() * currentH
    } else {
      this.y = -itemHeight - Math.random() * DECOR_RECYCLE_GAP
    }
    this.vy = SPEED_MIN + Math.random() * (SPEED_MAX - SPEED_MIN)

    // coin 垂直下落，无水平偏移
    if (this.type === 'coin') {
      this.vx = 0
      this.r = 0
    } else {
      this.vx = (Math.random() - 0.5) * DRIFT_MAX
      this.r = (Math.random() - 0.5) * 0.4
    }

    this.tailOffsetYDelta = (Math.random() - 0.5) * 8

    // lantern/coin 景深效果（仅部分装饰物有景深效果）
    if (
      (this.type === 'lantern' || this.type === 'coin') &&
      Math.random() < DEPTH_EFFECT_PROBABILITY
    ) {
      this.depthScale = DEPTH_SCALE_MIN + Math.random() * (DEPTH_SCALE_MAX - DEPTH_SCALE_MIN)
      const blurValue =
        DEPTH_BLUR_MIN +
        ((DEPTH_SCALE_MAX - this.depthScale) / (DEPTH_SCALE_MAX - DEPTH_SCALE_MIN)) *
          (DEPTH_BLUR_MAX - DEPTH_BLUR_MIN)
      this.blurIndex = getBlurIndex(blurValue)
    } else {
      this.depthScale = 1
      this.blurIndex = -1
    }
  }

  update() {
    this.y += this.vy
    this.x += this.vx
    if (this.y > H + 140) this.reset()
  }

  draw() {
    if (!ctx) return
    ctx.save()
    ctx.translate(this.x, this.y)
    ctx.rotate(this.r)

    // lantern/coin 应用景深效果
    if ((this.type === 'lantern' || this.type === 'coin') && this.blurIndex >= 0) {
      ctx.scale(this.depthScale, this.depthScale)
    }

    if (this.type === 'packet') {
      if (miniPacketCanvas) {
        ctx.drawImage(
          miniPacketCanvas,
          -MINI_PACKET_WIDTH / 2,
          -MINI_PACKET_HEIGHT / 2,
          MINI_PACKET_WIDTH,
          MINI_PACKET_HEIGHT
        )
      } else if (miniPacketImg.complete) {
        ctx.drawImage(
          miniPacketImg,
          -MINI_PACKET_WIDTH / 2,
          -MINI_PACKET_HEIGHT / 2,
          MINI_PACKET_WIDTH,
          MINI_PACKET_HEIGHT
        )
      }
    } else if (this.type === 'coin') {
      // 金币
      const blurCanvas = this.blurIndex >= 0 ? coinBlurCanvases[this.blurIndex] : null
      if (blurCanvas) {
        ctx.drawImage(blurCanvas, -COIN_WIDTH / 2, -COIN_HEIGHT / 2, COIN_WIDTH, COIN_HEIGHT)
      } else if (coinImg.complete) {
        ctx.drawImage(coinImg, -COIN_WIDTH / 2, -COIN_HEIGHT / 2, COIN_WIDTH, COIN_HEIGHT)
      }
    } else if (lanternImg.complete) {
      // 灯笼
      const blurCanvas = this.blurIndex >= 0 ? lanternBlurCanvases[this.blurIndex] : null
      if (blurCanvas) {
        ctx.drawImage(
          blurCanvas,
          -LANTERN_WIDTH / 2,
          -LANTERN_HEIGHT / 2,
          LANTERN_WIDTH,
          LANTERN_HEIGHT
        )
      } else {
        ctx.drawImage(
          lanternImg,
          -LANTERN_WIDTH / 2,
          -LANTERN_HEIGHT / 2,
          LANTERN_WIDTH,
          LANTERN_HEIGHT
        )
      }
    }
    ctx.restore()
  }
}

/**
 * HUD反馈动画
 */
function triggerCountJump() {
  isCountJumping.value = true
  if (countJumpTimer !== null) {
    clearTimeout(countJumpTimer)
    countJumpTimer = null
  }
  countJumpTimer = window.setTimeout(() => {
    isCountJumping.value = false
    countJumpTimer = null
  }, COUNT_JUMP_HOLD_MS)
}

function triggerCountFlash() {
  if (countFlashTimer !== null) {
    clearTimeout(countFlashTimer)
    countFlashTimer = null
  }
  isCountFlashing.value = false
  requestAnimationFrame(() => {
    isCountFlashing.value = true
    countFlashTimer = window.setTimeout(() => {
      isCountFlashing.value = false
      countFlashTimer = null
    }, COUNT_FLASH_MS)
  })
}

/**
 * 处理点击红包（支持触摸和鼠标）
 */
function handleClick(e: TouchEvent | MouseEvent) {
  if (!canvasRef.value) return
  const r = canvasRef.value.getBoundingClientRect()
  let x: number
  let y: number

  if ('touches' in e) {
    if (e.touches.length === 0) return
    x = e.touches[0].clientX - r.left
    y = e.touches[0].clientY - r.top
  } else {
    x = e.clientX - r.left
    y = e.clientY - r.top
  }

  for (const m of items) {
    if (!m.collected && !m.dead && Math.abs(m.x - x) < 45 && Math.abs(m.y - y) < 45) {
      m.collected = true
      break
    }
  }
}

function handlePointerDown(e: PointerEvent) {
  // 触摸设备继续使用 touchstart，避免同一次触摸被两套事件重复处理。
  if (e.pointerType === 'touch') return
  handleClick(e)
  resetCanvasCursor()
}

function handlePointerMove(e: PointerEvent) {
  if (!canvasRef.value || e.pointerType === 'touch') return
  const r = canvasRef.value.getBoundingClientRect()
  const x = e.clientX - r.left
  const y = e.clientY - r.top
  const isHoveringPacket = items.some(
    (m) => !m.collected && !m.dead && Math.abs(m.x - x) < 45 && Math.abs(m.y - y) < 45
  )

  // Canvas 内只有命中可点击红包时才展示手型。
  canvasRef.value.style.cursor = isHoveringPacket ? 'pointer' : 'default'
}

function resetCanvasCursor() {
  if (canvasRef.value) canvasRef.value.style.cursor = 'default'
}

/**
 * 动画循环
 */
function loop() {
  if (!running || !ctx) return

  // 更新剩余时间
  const now = Date.now()
  const remainingMs = endTime - now
  if (remainingMs <= 0) {
    // 时间到，结束动画
    stopAnimation()
    finishRain(hitCount.value)
    return
  }

  const seconds = Math.max(0, Math.ceil(remainingMs / 1000))
  if (seconds !== remainingSeconds.value) {
    remainingSeconds.value = seconds
  }

  ctx.clearRect(0, 0, W, H)
  decorItems.forEach((m) => {
    m.update()
    m.draw()
  })
  items.forEach((m) => {
    m.update(collectTargetX, collectTargetY)
    m.draw()
  })
  rafId = requestAnimationFrame(loop)
}

/**
 * 开始动画
 */
function startAnimation() {
  if (running) return
  running = true
  interruptionHandled = false
  const duration = animationDuration.value || 30

  // 立即初始化剩余时间，防止首帧跳秒
  remainingSeconds.value = Math.ceil(duration)
  endTime = Date.now() + duration * 1000
  updateCollectTarget()

  // 初始化红包数组
  items.length = 0

  // 可收集红包数量（独立计算）
  const collectableCount = getRainCount(W || window.innerWidth, H || window.innerHeight)

  // 装饰物数量（红包数量的 30%）
  const decorCount = Math.round(collectableCount * DECOR_RATIO_TO_PACKET)

  // 装饰物内部分配
  const lanternCount = Math.round(decorCount * DECOR_LANTERN_RATIO)
  const miniPacketCount = Math.round(decorCount * DECOR_MINI_PACKET_RATIO)
  const coinCount = Math.round(decorCount * DECOR_COIN_RATIO)

  collectableColumnSampler.reset()
  decorColumnSampler.reset()

  decorItems.length = 0
  for (let i = 0; i < lanternCount; i++) {
    decorItems.push(new DecorItem('lantern'))
  }
  for (let i = 0; i < miniPacketCount; i++) {
    decorItems.push(new DecorItem('packet'))
  }
  for (let i = 0; i < coinCount; i++) {
    decorItems.push(new DecorItem('coin'))
  }

  for (let i = 0; i < collectableCount; i++) {
    items.push(
      new Money(() => {
        hitCount.value++
        triggerCountJump()
        triggerCountFlash()
      })
    )
  }
  // 重置命中数
  hitCount.value = 0

  loop()
}

/**
 * 停止动画
 */
function stopAnimation() {
  running = false
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  if (countJumpTimer !== null) {
    clearTimeout(countJumpTimer)
    countJumpTimer = null
  }
  if (countFlashTimer !== null) {
    clearTimeout(countFlashTimer)
    countFlashTimer = null
  }
  items.length = 0
  decorItems.length = 0
  if (canvasRef.value) {
    canvasRef.value.width = 1
    canvasRef.value.height = 1
  }
}

function requestImmediateFinish() {
  if (!running || interruptionHandled) return
  interruptionHandled = true
  stopAnimation()
  finishRain(hitCount.value)
}

function handleVisibilityChange() {
  if (document.hidden) requestImmediateFinish()
}

function handleOffline() {
  requestImmediateFinish()
}

function handlePageHide() {
  requestImmediateFinish()
}

// 监听shouldShowRainCanvas变化
watch(
  shouldShowRainCanvas,
  (newVal) => {
    if (newVal) {
      // 显示时初始化并开始
      nextTick(() => {
        handleResize()
        startAnimation()
      })
    } else {
      // 隐藏时停止
      stopAnimation()
    }
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('resize', handleResize)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('offline', handleOffline)
  window.addEventListener('pagehide', handlePageHide)
  if (canvasRef.value) {
    canvasRef.value.addEventListener('touchstart', handleClick)
    // PC 端使用这个事件 及时反馈
    canvasRef.value.addEventListener('pointerdown', handlePointerDown)
    // cursor pointer
    canvasRef.value.addEventListener('pointermove', handlePointerMove)
    canvasRef.value.addEventListener('pointerleave', resetCanvasCursor)
  }
})

onBeforeUnmount(() => {
  stopAnimation()
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('offline', handleOffline)
  window.removeEventListener('pagehide', handlePageHide)
  if (canvasRef.value) {
    canvasRef.value.removeEventListener('touchstart', handleClick)
    canvasRef.value.removeEventListener('pointerdown', handlePointerDown)
    canvasRef.value.removeEventListener('pointermove', handlePointerMove)
    canvasRef.value.removeEventListener('pointerleave', resetCanvasCursor)
  }
})

const handleClose = () => {
  stopAnimation()
  finishRain(hitCount.value)
}
</script>

<style scoped lang="scss">
.rain-canvas {
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  box-shadow:
    0px 0px 200px 0px rgba(255, 78, 68, 0.2) inset,
    0px 0px 30px 0px rgba(255, 120, 2, 0.1) inset;
  overflow: hidden;
  touch-action: none;
}

.canvas {
  position: fixed;
  inset: 0;
}

.top-bar {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 132px;
  align-self: stretch;
  background:
    url('@/assets/imgs/activity/rain/curtain_bg.webp') bottom / 100% 132px no-repeat,
    linear-gradient(
      180deg,
      #b72127 0%,
      #b72127 50%,
      rgba(222, 84, 9, 0) 50%,
      rgba(222, 84, 9, 0) 100%
    );
}

.top-bar-drop-enter-active {
  animation: topBarDrop 0.7s cubic-bezier(0.16, 0.9, 0.2, 1) both;
}

@keyframes topBarDrop {
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.top-bar-drop-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.top-bar-drop-leave-to {
  transform: translateY(-40%);
  opacity: 0;
}

.collect-bar {
  width: 304px;
  height: 64px;
  display: flex;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  background-image: url('@/assets/imgs/activity/rain/pool_bg.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  .tip {
    margin-top: 3px;
    color: #fff;
  }

  .count-wrap {
    display: inline-flex;
    align-items: flex-end;
    height: 1em;
    line-height: 1;
    vertical-align: baseline;
  }

  .count {
    display: inline-block;
    color: #fff96a;
    padding: 0 3px;
    transform-origin: 50% 90%;
    transition:
      transform 180ms cubic-bezier(0.22, 0.9, 0.26, 1),
      opacity 120ms ease;
    will-change: transform, opacity;
  }

  .count.hold {
    transform: translateY(-2px) scale(1.03);
  }

  .count-inner {
    display: inline-block;
    transition: opacity 120ms ease;
  }

  .count-inner.flash {
    animation: countPop 140ms ease-out;
  }
}

@keyframes countPop {
  0% {
    transform: translateY(0) scale(1);
    opacity: 0.95;
  }

  60% {
    transform: translateY(-1.5px) scale(1.05);
    opacity: 1;
  }

  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

.time-block {
  display: flex;
  padding: 2px 14px;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  border-radius: 60px;
  min-width: 107px;
  border: 1px solid transparent;
  background:
    linear-gradient(#dc420a, #dc420a) border-box padding-box,
    linear-gradient(180deg, #ffe23f 0%, #ffe23f 100%) border-box;
  box-shadow:
    0px 0px 6px 0px #a22928,
    rgba(251, 86, 86, 0.25),
    0px rgba(255, 171, 36, 0.6),
    0px rgba(255, 196, 19, 0.8),
    0px rgba(255, 231, 48, 0.6);
  margin-bottom: 10px;
}

.time-text {
  font-size: 16px;
  font-weight: 700;
  color: #ffd66b;
  margin-left: 6px;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}

@keyframes hudPulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.12);
  }

  100% {
    transform: scale(1);
  }
}

@keyframes hudGlow {
  0% {
    box-shadow: 0 0 0 rgba(255, 214, 107, 0);
  }

  50% {
    box-shadow: 0 0 28px rgba(255, 214, 107, 0.75);
  }

  100% {
    box-shadow: 0 0 0 rgba(255, 214, 107, 0);
  }
}
</style>
